/**
 * The saved camera and microphone on/off, shared by the Join screen and the call.
 *
 * `preferVideoEnabled` / `preferAudioEnabled` hold what the Join screen showed,
 * and LiveKit starts the session from them alone, so a device shown off is
 * saved off and never goes live at join. An off can be the user's choice, or
 * forced by an error (a denied prompt, a busy camera). The `…Forced` marker
 * tells the two apart, so a forced off does not carry over to the next Join
 * screen while a chosen off does.
 */

export type MediaPreferenceKind = 'video' | 'audio'

const ENABLED_KEY: Record<MediaPreferenceKind, string> = {
  video: 'preferVideoEnabled',
  audio: 'preferAudioEnabled',
}

const FORCED_KEY: Record<MediaPreferenceKind, string> = {
  video: 'preferVideoEnabledForced',
  audio: 'preferAudioEnabledForced',
}

/**
 * Save a device's on/off. `forced`: the off was caused by an error, not
 * chosen. Every write without it is a choice and drops an earlier marker, so
 * a later deliberate off sticks.
 */
export function writeMediaPreference(
  kind: MediaPreferenceKind,
  enabled: boolean,
  { forced = false }: { forced?: boolean } = {}
): void {
  localStorage.setItem(ENABLED_KEY[kind], String(enabled))
  if (!enabled && forced) {
    localStorage.setItem(FORCED_KEY[kind], 'true')
  } else {
    localStorage.removeItem(FORCED_KEY[kind])
  }
}

/**
 * Whether the Join screen starts with the device on: unless the user turned
 * it off. A forced off is tried again; if the error is still there, it is
 * forced off again.
 */
export function readMediaPreferenceForJoin(kind: MediaPreferenceKind): boolean {
  if (localStorage.getItem(ENABLED_KEY[kind]) !== 'false') return true
  return localStorage.getItem(FORCED_KEY[kind]) === 'true'
}
